// components/ImageUploader.jsx
import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react';
import styles from '../CSS/ImageUploader.module.css';

const ImageUploader = ({ currentImage, onImageChange, disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten imágenes JPEG, PNG o WEBP');
      return;
    }

    // Validar tamaño (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede superar los 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      console.log('📤 Subiendo imagen a /api/upload.php...');

      // 🎯 Subir al PHP local (mismo dominio)
      const response = await fetch('/api/upload.php', {
        method: 'POST',
        body: formData
      });

      console.log('📥 Status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error del servidor:', errorText);
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Respuesta:', data);

      if (data.success) {
        // URL relativa (ya estamos en el mismo dominio)
        setPreview(data.imageUrl);
        onImageChange(data.imageUrl); // Guardar ruta relativa en Firebase
        console.log('✅ Imagen subida:', data.imageUrl);
      } else {
        throw new Error(data.message || 'Error al subir la imagen');
      }
    } catch (err) {
      console.error('❌ Error completo:', err);
      setError(err.message || 'Error al subir la imagen. Verifica la consola.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={styles.uploaderContainer}>
      <label className={styles.label}>
        <ImageIcon className={styles.labelIcon} />
        Imagen del espacio (opcional)
      </label>

      <div className={styles.uploaderContent}>
        {preview ? (
          <div className={styles.previewContainer}>
            <img 
              src={preview} 
              alt="Preview" 
              className={styles.previewImage}
              onError={(e) => {
                console.error('❌ Error cargando imagen:', preview);
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
              }}
            />
            <button
              type="button"
              className={styles.removeButton}
              onClick={handleRemoveImage}
              disabled={disabled || uploading}
              title="Eliminar imagen"
            >
              <X className={styles.removeIcon} />
            </button>
          </div>
        ) : (
          <div className={styles.uploadArea} onClick={handleButtonClick}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              onChange={handleFileSelect}
              disabled={disabled || uploading}
              className={styles.fileInput}
            />
            
            {uploading ? (
              <div className={styles.uploadingState}>
                <Loader className={styles.loadingIcon} />
                <span>Subiendo imagen...</span>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Upload className={styles.uploadIcon} />
                <span className={styles.uploadText}>
                  Haz clic para seleccionar una imagen
                </span>
                <span className={styles.uploadHint}>
                  JPEG, PNG o WEBP (máx. 5MB)
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className={styles.errorMessage}>
          ⚠️ {error}
        </div>
      )}

      <p className={styles.helperText}>
        La imagen se mostrará como fondo de la tarjeta del espacio
      </p>
    </div>
  );
};

export default ImageUploader;
